import axios from 'axios';
import Project from '@theme/global-functions';
import Splide from '@splidejs/splide';

class DynamicBoxes {
  static displayedPostIDs = new Set();

  constructor(element) {
    this.element = element;
    this.resultsContainer = element.querySelector('.posts-result__container');
    this.loader = element.querySelector('.dynamic-boxes-block__loader');
    this.viewType = element.dataset.view || 'grid';
    this.paginationType = element.dataset.paginationType || 'off';

    this.paged = 1;
    this.isLoading = false;
    this.splideInstance = null;

    this.paginationContainer = element.querySelector('.pagination');
    this.loadMore = element.querySelector(
      '.dynamic-boxes-block__load-more-wrapper'
    );
    this.loadMoreButton = this.loadMore?.querySelector('#load-more');
    this.infinityScrollObserver = element.querySelector(
      '.dynamic-boxes-block__infinity-scroll-observer'
    );
    this.infinityScrollLoader = element.querySelector(
      '.dynamic-boxes-block__infinity-scroll-loader'
    );
    this.infinityScrollButton = this.infinityScrollLoader?.querySelector(
      '#infinity-scroll-button'
    );

    this.maxNumberPages = parseInt(element.dataset.maxNumberPages, 10) || 1;
    this.postCount = parseInt(element.dataset.postCount, 10) || 10;
    this.postType = element.dataset.postType || null;
    this.postSort = element.dataset.postSort || 'DESC';
    this.postOffset = parseInt(element.dataset.offset, 10) || 0;
    this.postTerms = element.dataset.postTerms
      ? element.dataset.postTerms.split(',')
      : [];
    this.htmlTag = element.dataset.htmlTag || 'article';
    this.boxClickable = element.dataset.boxClickable || 'false';
    this.chooseElements = element.dataset.boxElements || null;
    this.isArchive = element.dataset.isArchive || 'false';
    this.postTaxonomy = element.dataset.taxonomy || null;
    this.loadMoreLabel = element.dataset.loadMoreLabel || 'Pokaż więcej';

    this.addInitialPostsToGlobalSet();
  }

  addInitialPostsToGlobalSet() {
    const initialBoxes = this.element.querySelectorAll('[data-post-id]');
    initialBoxes.forEach((box) => {
      const postId = parseInt(box.dataset.postId, 10);
      if (postId) {
        DynamicBoxes.displayedPostIDs.add(postId);
      }
    });
  }

  filterUniqueHTML(html) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const incomingBoxes = tempDiv.querySelectorAll('[data-post-id]');

    incomingBoxes.forEach((box) => {
      const postId = parseInt(box.dataset.postId, 10);
      if (DynamicBoxes.displayedPostIDs.has(postId)) {
        box.remove();
      } else {
        DynamicBoxes.displayedPostIDs.add(postId);
      }
    });

    return tempDiv.innerHTML;
  }

  handleLoadMoreResponse(data) {
    const { html, has_more, max_num_pages } = data;
    this.maxNumberPages = parseInt(max_num_pages, 10);

    const uniqueHTML = this.filterUniqueHTML(html);

    if (uniqueHTML.trim() !== '') {
      this.resultsContainer.insertAdjacentHTML('beforeend', uniqueHTML);
    } else if (this.paged < this.maxNumberPages && !this.isLoading) {
      this.paged++;
      this.loadContent(false);
      return;
    }

    const morePostsExist = this.paged < this.maxNumberPages;
    if (this.hasLoadMore) {
      this.loadMore.style.display = morePostsExist ? 'block' : 'none';
      if (morePostsExist) this.loadMoreButton.textContent = this.loadMoreLabel;
    }
    if (this.hasInfinityScroll) {
      this.infinityScrollLoader.style.display = morePostsExist
        ? 'flex'
        : 'none';
      if (morePostsExist)
        this.infinityScrollButton.textContent = this.loadMoreLabel;
    }
  }

  handlePaginationResponse(data) {
    const { html, pagination, max_num_pages } = data;
    this.maxNumberPages = parseInt(max_num_pages, 10);
    this.resultsContainer.innerHTML = html;
    if (this.paginationContainer) {
      this.paginationContainer.innerHTML = pagination;
    }
    window.scrollTo({ top: this.element.offsetTop - 100, behavior: 'smooth' });
    this.initPagination();
  }

  init() {
    if (this.viewType === 'slider') {
      this.initSlider();
    } else if (this.element.dataset.gridResponsiveBehavior !== 'grid') {
      this.initResponsiveGrid();
    }
    if (this.viewType !== 'slider') {
      this.initPaginationFeatures();
    }
  }

  _getSplideOptions() {
    return {
      type: this.element.dataset.splideRewind === 'true' ? 'loop' : 'slide',
      autoplay: this.element.dataset.splideAutoplay === 'true',
      interval: parseInt(this.element.dataset.splideAutoplaySpeed, 10) || 4000,
      arrows: this.element.dataset.splideArrows === 'true',
      pagination: this.element.dataset.splidePagination === 'true',
      perPage: parseInt(this.element.dataset.splideDesktop, 10) || 3,
      gap: '24px',
      breakpoints: {
        1024: {
          perPage: parseInt(this.element.dataset.splideTablet, 10) || 2,
          gap: '16px',
        },
        790: {
          perPage: parseInt(this.element.dataset.splideMobile, 10) || 1,
          gap: '16px',
          autoWidth: true,
          focus: 'center',
        },
      },
    };
  }

  initSlider() {
    if (!this.resultsContainer) return;
    const options = this._getSplideOptions();
    this.splideInstance = new Splide(this.resultsContainer, options);
    this.splideInstance.mount();
  }

  initResponsiveGrid() {
    const behavior = this.element.dataset.gridResponsiveBehavior;
    if (behavior === 'grid' || !this.resultsContainer) return;

    const tabletBreakpoint = window.matchMedia('(max-width: 1024px)');
    const mobileBreakpoint = window.matchMedia('(max-width: 789px)');

    const handleResize = () => {
      const isTablet = tabletBreakpoint.matches;
      const isMobile = mobileBreakpoint.matches;

      let shouldBeSlider =
        (behavior === 'slider_tablet' && isTablet) ||
        (behavior === 'slider_mobile' && isMobile);

      if (shouldBeSlider && !this.splideInstance) {
        this.resultsContainer.classList.add('splide');
        const options = this._getSplideOptions();
        this.splideInstance = new Splide(this.resultsContainer, options);
        this.splideInstance.mount();
      } else if (!shouldBeSlider && this.splideInstance) {
        this.splideInstance.destroy();
        this.splideInstance = null;
        this.resultsContainer.classList.remove('splide');
      }
    };

    tabletBreakpoint.addEventListener('change', handleResize);
    mobileBreakpoint.addEventListener('change', handleResize);

    handleResize();
  }

  get hasLoadMore() {
    return this.loadMore !== null && this.paginationType === 'load-more';
  }
  get hasPagination() {
    return (
      this.paginationContainer !== null && this.paginationType === 'numbers'
    );
  }
  get hasInfinityScroll() {
    return (
      this.infinityScrollObserver !== null &&
      this.paginationType === 'infinity-scroll'
    );
  }

  checkPostSort() {
    let postSortType = 'DESC';
    let postOrderBy = 'date';
    switch (this.postSort) {
      case 'ASC':
        postSortType = 'ASC';
        postOrderBy = 'date';
        break;
      case 'DESC':
        postSortType = 'DESC';
        postOrderBy = 'date';
        break;
      case 'title_desc':
        postSortType = 'DESC';
        postOrderBy = 'title';
        break;
      case 'title_asc':
        postSortType = 'ASC';
        postOrderBy = 'title';
        break;
    }
    return { postSortType, postOrderBy };
  }

  async loadContent(isPaginationAction = false) {
    if (this.isLoading) return;
    this.isLoading = true;
    this.showLoader(isPaginationAction);
    const { postSortType, postOrderBy } = this.checkPostSort();
    const formData = this.prepareFormData(postOrderBy, postSortType);
    const action = isPaginationAction
      ? 'dynamic_boxes_pagination'
      : 'dynamic_boxes_load_more';
    formData.append('action', action);
    try {
      const response = await axios.post(window.wp_app_vars.ajax_url, formData);
      if (response.data.success) {
        if (isPaginationAction) {
          this.handlePaginationResponse(response.data.data);
        } else {
          this.handleLoadMoreResponse(response.data.data);
        }
      } else {
        throw new Error(response.data.data?.message || 'Błąd zapytania AJAX.');
      }
    } catch (error) {
      console.error('Błąd Dynamic Boxes:', error);
    } finally {
      this.isLoading = false;
      this.hideLoader(isPaginationAction);
    }
  }

  prepareFormData(postOrderBy, postSortType) {
    const formData = new FormData();
    formData.append('nonce', window.wp_app_vars.nonce);
    formData.append('paged', this.paged);
    formData.append('posts_per_page', this.postCount);
    formData.append('post_type', this.postType);
    formData.append('post_offset', this.postOffset);
    formData.append('is_archive', this.isArchive);
    formData.append('post_taxonomy', this.postTaxonomy);
    formData.append('post_terms', this.postTerms.join(','));
    formData.append('elements', this.chooseElements);
    formData.append('html_tag', this.htmlTag);
    formData.append('is_clickable', this.boxClickable);
    formData.append('pagination_type', this.paginationType);
    formData.append('order_by', postOrderBy);
    formData.append('order', postSortType);
    formData.append('load_more_label', this.loadMoreLabel);
    return formData;
  }

  showLoader(isPagination) {
    if (isPagination) {
      this.loader.style.display = 'block';
      this.resultsContainer.style.opacity = '0.5';
    } else if (this.hasLoadMore && this.loadMoreButton) {
      this.loadMoreButton.disabled = true;
      this.loadMoreButton.textContent = 'Ładowanie...';
    } else if (this.hasInfinityScroll && this.infinityScrollButton) {
      this.infinityScrollLoader.style.display = 'flex';
      this.infinityScrollButton.textContent = 'Ładowanie...';
    }
  }

  hideLoader(isPagination) {
    if (isPagination) {
      this.loader.style.display = 'none';
      this.resultsContainer.style.opacity = '1';
    } else if (this.hasLoadMore && this.loadMoreButton) {
      this.loadMoreButton.disabled = false;
    }
  }

  initPaginationFeatures() {
    if (this.hasPagination) this.initPagination();
    if (this.hasLoadMore) this.initLoadMore();
    if (this.hasInfinityScroll) this.initInfinityScroll();
  }

  initLoadMore() {
    if (!this.loadMoreButton) return;
    this.loadMoreButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.isLoading) return;
      this.paged++;
      this.loadContent(false);
    });
    if (this.paged >= this.maxNumberPages) {
      this.loadMore.style.display = 'none';
    }
  }

  initPagination() {
    const links = this.paginationContainer?.querySelectorAll('a');
    if (!links) return;
    links.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetPage = e.currentTarget.dataset.page;
        let newPage = this.paged;
        if (targetPage === 'next') {
          if (this.paged < this.maxNumberPages) newPage++;
        } else if (targetPage === 'prev') {
          if (this.paged > 1) newPage--;
        } else {
          newPage = parseInt(targetPage, 10);
        }
        if (newPage !== this.paged && !this.isLoading) {
          this.paged = newPage;
          this.loadContent(true);
        }
      });
    });
  }

  initInfinityScroll() {
    if (!this.infinityScrollObserver || !this.infinityScrollButton) return;
    this.infinityScrollButton.textContent = this.loadMoreLabel;
    if (this.paged >= this.maxNumberPages) {
      this.infinityScrollLoader.style.display = 'none';
    } else {
      this.infinityScrollLoader.style.display = 'flex';
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !this.isLoading &&
          this.paged < this.maxNumberPages
        ) {
          this.paged++;
          this.loadContent(false);
        }
      },
      { rootMargin: '0px', threshold: 0.1 }
    );
    observer.observe(this.infinityScrollObserver);
  }
}

Project.ready(() => {
  document.querySelectorAll('.dynamic-boxes-block').forEach((module) => {
    const instance = new DynamicBoxes(module);
    instance.init();
    module.dynamicBoxesInstance = instance;
  });
});

export default DynamicBoxes;
